import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import {
  useGetAvailableAssigneesQuery,
  useGetPropertyRolesQuery,
} from '@/store/api/admin/todoListsApi';

interface Props {
  propertyId: number;
  roleId: number | null;
  assigneeAccountId: number | null;
  onSelectRole: (roleId: number) => void;
  onSelectPerson: (accountId: number) => void;
  error?: string;
}

/**
 * XOR target picker for a to-do list. The role branch is fed by
 * `useGetPropertyRolesQuery` (F26) and the person branch by
 * `useGetAvailableAssigneesQuery` (F27), both scoped to `propertyId`.
 *
 * The two branches are mutually exclusive: once a role is chosen the person
 * options are disabled (and vice-versa), mirroring the server's role/assignee
 * XOR. Selecting one clears the other (handled by the parent's onSelect*).
 */
export const TodoTargetPicker: React.FC<Props> = ({
  propertyId,
  roleId,
  assigneeAccountId,
  onSelectRole,
  onSelectPerson,
  error,
}) => {
  const { redesign } = useAdminColors();
  // Skip until a property is resolved — mirrors the occurrence screen's guard so
  // we never fire a request with propertyId 0 while the context is loading.
  const { data: roles } = useGetPropertyRolesQuery(
    { propertyId },
    { skip: !propertyId },
  );
  const { data: assignees } = useGetAvailableAssigneesQuery(
    { propertyId },
    { skip: !propertyId },
  );

  const roleChosen = roleId != null;
  const personChosen = assigneeAccountId != null;

  return (
    <View style={styles.wrap}>
      <Text style={[styles.sectionLabel, { color: redesign.text.secondary }]}>
        Assign to
      </Text>
      <Text style={[styles.hint, { color: redesign.text.muted }]}>
        Choose exactly one — a role or a specific person.
      </Text>

      <Text style={[styles.branchLabel, { color: redesign.text.subtle }]}>
        Role
      </Text>
      <View style={styles.options}>
        {(roles ?? []).map(role => {
          const selected = roleId === role.id;
          const optionStyle = {
            borderColor: selected
              ? redesign.accent.purpleLight
              : redesign.card.border,
            backgroundColor: selected ? redesign.accent.blueBg : 'transparent',
            opacity: personChosen ? 0.4 : 1,
          };
          const textColor = selected
            ? redesign.text.primary
            : redesign.text.secondary;
          return (
            <Pressable
              key={role.id}
              testID={`role-option-${role.id}`}
              disabled={personChosen}
              onPress={() => onSelectRole(role.id)}
              style={[styles.option, optionStyle]}
            >
              <Text style={[styles.optionText, { color: textColor }]}>
                {role.name ?? `Role ${role.id}`}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <Text style={[styles.branchLabel, { color: redesign.text.subtle }]}>
        Person
      </Text>
      <View style={styles.options}>
        {(assignees ?? []).map(person => {
          const selected = assigneeAccountId === person.id;
          const name = `${person.firstName} ${person.lastName}`.trim();
          const optionStyle = {
            borderColor: selected
              ? redesign.accent.purpleLight
              : redesign.card.border,
            backgroundColor: selected ? redesign.accent.blueBg : 'transparent',
            opacity: roleChosen ? 0.4 : 1,
          };
          const textColor = selected
            ? redesign.text.primary
            : redesign.text.secondary;
          return (
            <Pressable
              key={person.id}
              testID={`person-option-${person.id}`}
              disabled={roleChosen}
              onPress={() => onSelectPerson(person.id)}
              style={[styles.option, optionStyle]}
            >
              <Text style={[styles.optionText, { color: textColor }]}>
                {name || person.email}
              </Text>
            </Pressable>
          );
        })}
      </View>

      {error ? (
        <Text style={[styles.error, { color: redesign.accent.red }]}>
          {error}
        </Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { gap: 8 },
  sectionLabel: { fontSize: 14, fontWeight: '700' },
  hint: { fontSize: 12 },
  branchLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginTop: 4,
  },
  options: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  optionText: { fontSize: 14, fontWeight: '600' },
  error: { fontSize: 12, marginTop: 2 },
});
